'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

interface AnimationProps {
  children: ReactNode;
  delay?: number;
  className?: string;
}

// Out-expo — matches the DESIGN.md atmospheric/cinematic curve.
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

/**
 * Scroll-reveal that is VISIBLE BY DEFAULT.
 *
 * The server-rendered DOM (and any no-JS / crawler / headless render) shows the
 * content at full opacity with no transform — so a section never ships blank
 * (impeccable: "reveal animations must enhance an already-visible default").
 *
 * The entrance is a progressive enhancement applied only after hydration, and
 * only to elements that are BELOW the fold at mount — those are off-screen, so
 * hiding-then-revealing them causes no visible flash. Elements already in view
 * at mount stay visible (no flash, no entrance). Reduced-motion keeps everything
 * static.
 */
function Reveal({
  children,
  delay = 0,
  className = '',
  hiddenTransform,
  duration,
}: AnimationProps & { hiddenTransform: string; duration: number }) {
  const ref = useRef<HTMLDivElement>(null);
  // 'static' = visible, no transition (the SSR / no-JS default).
  // 'hidden' = off-screen and waiting to reveal. 'shown' = revealed.
  const [state, setState] = useState<'static' | 'hidden' | 'shown'>('static');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const rect = el.getBoundingClientRect();
    const inView = rect.top < window.innerHeight - 80 && rect.bottom > 0;
    // Above the fold at mount: leave it visible — never flash content the user
    // can already see.
    if (inView) return;

    // Below the fold: safe to hide now (off-screen), then reveal on scroll.
    setState('hidden');
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setState('shown');
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -80px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const isHidden = state === 'hidden';
  const animated = state !== 'static';

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isHidden ? 0 : 1,
        transform: isHidden ? hiddenTransform : 'none',
        transition: animated
          ? `opacity ${duration}s ${EASE}, transform ${duration}s ${EASE}`
          : undefined,
        transitionDelay: animated ? `${delay}s` : undefined,
        willChange: animated ? 'opacity, transform' : undefined,
      }}
    >
      {children}
    </div>
  );
}

export function FadeIn(props: AnimationProps) {
  return (
    <Reveal {...props} hiddenTransform="translateY(32px)" duration={1.2} />
  );
}

export function SlideIn(props: AnimationProps) {
  return <Reveal {...props} hiddenTransform="translateX(-24px)" duration={1} />;
}
